import { useState, useEffect, useCallback } from 'react';
import { safeJson, networkMessage } from '../apiHelpers';
import CustomOffersTab, { CustomRequestForm } from './CustomOffers';
import OrderChat from './OrderChat';
import Milestones from './Milestones';
import OrderDeadline from './OrderDeadline';
import PayOrderButton from './PayOrderButton';
import OrderCancel from './OrderCancel';
import { t } from '../i18n';
import PiPrice from './PiPrice';

const API_BASE = '/api/freelance';

function getAuthToken() {
  return localStorage.getItem('token') || '';
}

function authHeaders() {
  const token = getAuthToken();
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Token ${token}` } : {}),
  };
}

const colors = {
  bg: '#F7F5F0',
  surface: '#fff',
  border: '#E3E1D9',
  text: '#1F1E1B',
  textMuted: '#5F5E5A',
  primary: '#0F6E56',
  primaryBg: '#E1F5EE',
  danger: '#A32D2D',
};

const CATEGORIES = [
  ['design', 'Graphics & Design'],
  ['writing', 'Writing & Translation'],
  ['marketing', 'Digital Marketing'],
  ['video', 'Video & Animation'],
  ['music', 'Music & Audio'],
  ['programming', 'Programming & Tech'],
  ['business', 'Business & Consulting'],
  ['lifestyle', 'Lifestyle'],
  ['other', 'Other'],
];

function GigCard({ gig, onOpen, piRate }) {
  return (
    <div onClick={() => onOpen(gig.slug)} style={{ background: colors.surface, border: `0.5px solid ${colors.border}`, borderRadius: 12, overflow: 'hidden', cursor: 'pointer' }}>
      <div style={{ width: '100%', aspectRatio: '4/3', background: '#F1EFE8', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {gig.cover_image ? (
          <img src={gig.cover_image} alt={gig.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
          <span style={{ fontSize: 32, opacity: 0.3 }}>💼</span>
        )}
      </div>
      <div style={{ padding: '0.65rem' }}>
        {gig.is_boosted && (
          <span style={{ fontSize: 10, background: '#FFF3C4', color: '#8A6D00', padding: '2px 6px', borderRadius: 8, marginBottom: 4, display: 'inline-block' }}>
            📢 Sponsored
          </span>
        )}
        {gig.matched_interest && (
          <span style={{ fontSize: 10, background: colors.primaryBg, color: colors.primary, padding: '2px 6px', borderRadius: 8, marginBottom: 4, marginLeft: 4, display: 'inline-block' }}>
            ✨ For you
          </span>
        )}
        <p style={{ fontSize: 12, color: colors.textMuted, margin: '0 0 2px' }}>@{gig.freelancer_username}</p>
        <p style={{ fontSize: 13, fontWeight: 500, margin: '0 0 6px', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>{gig.title}</p>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {gig.avg_rating > 0 && (
            <span style={{ fontSize: 11, color: colors.textMuted }}>⭐ {gig.avg_rating} ({gig.total_reviews})</span>
          )}
          <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: colors.primary, display: 'block' }}>
              {gig.starting_price_pi ? <>From <PiPrice pi={gig.starting_price_pi} /></> : ''}
            </span>
            
          </div>
        </div>
      </div>
    </div>
  );
}

function GigDetailModal({ slug, onClose, onOrdered, piRate }) {
  const [gig, setGig] = useState(null);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [requirements, setRequirements] = useState('');
  const [ordering, setOrdering] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    fetch(`${API_BASE}/gigs/${slug}/`)
      .then((r) => r.json())
      .then((data) => {
        setGig(data);
        if (data.packages?.length) setSelectedPackage(data.packages[0]);
      })
      .catch(() => {});
  }, [slug]);

  const placeOrder = async () => {
    if (!getAuthToken()) {
      setMsg(t('fl_login'));
      return;
    }
    if (!window.Pi) {
      setMsg(t('fl_open_pi'));
      return;
    }
    setOrdering(true);
    setMsg('');

    try {
      await window.Pi.authenticate(['username', 'payments'], () => {});

      const res = await fetch(`${API_BASE}/orders/create/`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ package_id: selectedPackage.id, requirements }),
      });
      const data = await safeJson(res);
      if (!res.ok) throw new Error(data.error || 'Could not create order.');

      window.Pi.createPayment(
        {
          amount: data.price_pi,
          memo: `Gig order: ${gig.title}`,
          metadata: { order_id: data.order_id },
        },
        {
          onReadyForServerApproval: (paymentId) => {
            fetch(`${API_BASE}/orders/${data.order_id}/approve/`, {
              method: 'POST', headers: authHeaders(), body: JSON.stringify({ payment_id: paymentId }),
            });
          },
          onReadyForServerCompletion: (paymentId, txid) => {
            fetch(`${API_BASE}/orders/${data.order_id}/complete/`, {
              method: 'POST', headers: authHeaders(), body: JSON.stringify({ payment_id: paymentId, txid }),
            })
              .then(() => {
                setMsg('Order placed! Track it under Orders -> As Client.');
                setOrdering(false);
                onOrdered();
              })
              .catch(() => {
                setMsg('Payment reached Pi but order activation failed. Contact support.');
                setOrdering(false);
              });
          },
          onCancel: () => { setMsg(t('fl_cancelled')); setOrdering(false); },
          onError: () => { setMsg(t('fl_pay_err')); setOrdering(false); },
        }
      );
    } catch (e) {
      setMsg(e.message);
      setOrdering(false);
    }
  };

  const addToCart = async () => {
    if (!getAuthToken()) { setMsg(t('fl_login')); return; }
    if (!selectedPackage) { setMsg(t('fl_pick_pkg')); return; }
    try {
      await fetch(`/api/cart/add/`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({
          app_name: 'freelance', item_type: 'gig_package', reference_id: selectedPackage.id,
          title: `${gig.title} (${selectedPackage.name})`, thumbnail_url: gig.cover_image || '',
          price_pi: selectedPackage.price_pi, metadata: { requirements },
        }),
      });
      setMsg('Added to cart!');
      if (window.confirm('Added to cart. Go to your cart now?')) { window.location.href = '/cart'; }
    } catch (e) {
      setMsg(t('fl_cart_fail'));
    }
  };

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '20px 12px', overflowY: 'auto' }} onClick={onClose}>
      <div style={{ background: '#fff', borderRadius: 16, maxWidth: 560, width: '100%', padding: '1.5rem', marginTop: 20 }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ border: 'none', background: 'transparent', fontSize: 20, cursor: 'pointer', color: colors.textMuted }}>✕</button>
        </div>
        {!gig ? (
          <p style={{ textAlign: 'center', color: colors.textMuted }}>{t('loading')}</p>
        ) : (
          <>
            {gig.cover_image && <img src={gig.cover_image} alt={gig.title} style={{ width: '100%', borderRadius: 10, marginBottom: 12 }} />}
            <h2 style={{ fontSize: 18, margin: '0 0 4px' }}>{gig.title}</h2>
            <p style={{ fontSize: 13, color: colors.textMuted, margin: '0 0 4px' }}>
              by <a href={`/profile/${gig.freelancer_username}`} style={{ color: colors.primary }}>@{gig.freelancer_username}</a>
              {gig.freelancer_title && ` · ${gig.freelancer_title}`}
            </p>
            <a href={`/chat/${gig.freelancer_username}`} style={{ display: 'inline-block', fontSize: 12, color: colors.primary, marginBottom: 12 }}>
              💬 Contact freelancer
            </a>
            <CustomRequestForm gig={gig} />
            <p style={{ fontSize: 14, color: colors.text, lineHeight: 1.5, marginBottom: 16 }}>{gig.description}</p>

            <p style={{ fontSize: 13, fontWeight: 600, margin: '0 0 8px' }}>{t('choose_package')}</p>
            <div style={{ display: 'flex', gap: 8, marginBottom: 12, overflowX: 'auto' }}>
              {gig.packages.map((p) => (
                <button
                  key={p.id}
                  onClick={() => setSelectedPackage(p)}
                  style={{
                    flexShrink: 0, minWidth: 130, textAlign: 'left', padding: '10px 12px', borderRadius: 10, cursor: 'pointer',
                    border: selectedPackage?.id === p.id ? `2px solid ${colors.primary}` : `0.5px solid ${colors.border}`,
                    background: selectedPackage?.id === p.id ? colors.primaryBg : '#fff',
                  }}
                >
                  <p style={{ fontSize: 12, fontWeight: 600, margin: '0 0 4px', textTransform: 'uppercase' }}>{p.name}</p>
                  <p style={{ fontSize: 16, fontWeight: 600, color: colors.primary, margin: '0 0 2px' }}><PiPrice pi={p.price_pi} /></p>
                  <p style={{ fontSize: 11, color: colors.textMuted, margin: 0 }}>{p.delivery_days}d delivery · {p.revisions} revisions</p>
                </button>
              ))}
            </div>

            {selectedPackage && <p style={{ fontSize: 12, color: colors.textMuted, marginBottom: 12 }}>{selectedPackage.description}</p>}

            <label style={{ fontSize: 12, color: colors.textMuted, display: 'block', marginBottom: 4 }}>{t('fl_brief')}</label>
            <textarea
              value={requirements}
              onChange={(e) => setRequirements(e.target.value)}
              rows={3}
              style={{ width: '100%', padding: '8px 10px', fontSize: 13, border: `0.5px solid #B4B2A9`, borderRadius: 6, boxSizing: 'border-box', marginBottom: 12, resize: 'vertical' }}
              placeholder={t('describe_need')}
            />

            <button
              onClick={addToCart}
              disabled={!selectedPackage}
              style={{ width: '100%', padding: '12px', fontSize: 14, fontWeight: 500, border: 'none', borderRadius: 8, background: colors.primary, color: '#fff', cursor: 'pointer' }}
            >
              🛒 Add to Cart - <PiPrice pi={selectedPackage?.price_pi || 0} />
            </button>
            {msg && <p style={{ fontSize: 12, color: colors.danger, marginTop: 8 }}>{msg}</p>}

            {gig.reviews.length > 0 && (
              <div style={{ marginTop: 16, borderTop: `0.5px solid ${colors.border}`, paddingTop: 12 }}>
                <p style={{ fontSize: 13, fontWeight: 600, margin: '0 0 8px' }}>{t('reviews')}</p>
                {gig.reviews.map((r, i) => (
                  <p key={i} style={{ fontSize: 12, color: colors.textMuted, margin: '4px 0' }}>
                    @{r.reviewer} {'⭐'.repeat(r.rating)} {r.comment}
                  </p>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function CreateGigForm({ onGigCreated }) {
  const [form, setForm] = useState({ title: '', category: 'other', description: '' });
  const [packages, setPackages] = useState({
    basic: { price_pi: '', delivery_days: 3, revisions: 1, description: '' },
  });
  const [enabledTiers, setEnabledTiers] = useState({ basic: true, standard: false, premium: false });
  const [imageFile, setImageFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const updateForm = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const updatePkg = (tier, key) => (e) => setPackages((p) => ({ ...p, [tier]: { ...(p[tier] || {}), [key]: e.target.value } }));

  const toggleTier = (tier) => {
    setEnabledTiers((t) => ({ ...t, [tier]: !t[tier] }));
    if (!packages[tier]) {
      setPackages((p) => ({ ...p, [tier]: { price_pi: '', delivery_days: 5, revisions: 2, description: '' } }));
    }
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const pkgList = Object.entries(enabledTiers)
        .filter(([, on]) => on)
        .map(([tier]) => ({
          tier, name: tier.charAt(0).toUpperCase() + tier.slice(1),
          price_pi: parseFloat(packages[tier].price_pi) || 0,
          delivery_days: parseInt(packages[tier].delivery_days) || 3,
          revisions: parseInt(packages[tier].revisions) || 1,
          description: packages[tier].description || '',
        }));

      if (pkgList.length === 0 || !pkgList[0].price_pi) {
        alert('At least the Basic package needs a price.');
        setSubmitting(false);
        return;
      }

      const res = await fetch(`${API_BASE}/gigs/create/`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ ...form, packages: pkgList }),
      });
      let data = {};
      try { data = await safeJson(res); } catch (parseErr) { data = {}; }
      if (!res.ok) {
        // Asal wajah dikhao - backend ka 'error', DRF ka 'detail', ya status ke hisab se saaf paigham
        let reason = data.error || data.detail || data.message;
        if (!reason) {
          if (res.status === 401 || res.status === 403) reason = 'Your session expired - please log in again with Pi and retry.';
          else if (res.status === 413) reason = 'Your image is too large - try a smaller one.';
          else if (res.status >= 500) reason = 'Server error while creating the gig. Please try again in a moment.';
          else reason = `Could not create gig (error ${res.status}).`;
        }
        throw new Error(reason);
      }

      if (imageFile) {
        const formData = new FormData();
        formData.append('image', imageFile);
        const token = getAuthToken();
        await fetch(`${API_BASE}/gigs/${data.id}/upload-image/`, {
          method: 'POST', headers: token ? { Authorization: `Token ${token}` } : {}, body: formData,
        });
      }

      alert('Gig created!');
      setForm({ title: '', category: 'other', description: '' });
      setPackages({ basic: { price_pi: '', delivery_days: 3, revisions: 1, description: '' } });
      setEnabledTiers({ basic: true, standard: false, premium: false });
      setImageFile(null);
      onGigCreated();
    } catch (e) {
      alert(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const inputStyle = { width: '100%', padding: '8px 10px', fontSize: 14, border: '0.5px solid #B4B2A9', borderRadius: 6, boxSizing: 'border-box' };
  const labelStyle = { fontSize: 13, color: colors.textMuted, display: 'block', marginBottom: 4 };

  return (
    <div style={{ background: '#F1EFE8', border: `0.5px solid ${colors.border}`, borderRadius: 12, padding: '1.25rem' }}>
      <p style={{ fontWeight: 500, fontSize: 15, margin: '0 0 12px' }}>{t('create_gig')}</p>

      <div style={{ display: 'grid', gap: 10, marginBottom: 16 }}>
        <div>
          <label style={labelStyle}>{t('gig_title')}</label>
          <input style={inputStyle} value={form.title} onChange={updateForm('title')} placeholder="I will design a modern logo for your brand" />
        </div>
        <div>
          <label style={labelStyle}>{t('category_label')}</label>
          <select style={inputStyle} value={form.category} onChange={updateForm('category')}>
            {CATEGORIES.map(([val, label]) => <option key={val} value={val}>{label}</option>)}
          </select>
        </div>
        <div>
          <label style={labelStyle}>{t('description')}</label>
          <textarea style={{ ...inputStyle, resize: 'vertical' }} rows={3} value={form.description} onChange={updateForm('description')} />
        </div>
        <div>
          <label style={labelStyle}>{t('cover_image')}</label>
          <input type="file" accept="image/*" onChange={(e) => setImageFile(e.target.files[0])} />
        </div>
      </div>

      <p style={{ fontWeight: 500, fontSize: 14, margin: '0 0 8px' }}>{t('packages_label')}</p>
      {['basic', 'standard', 'premium'].map((tier) => (
        <div key={tier} style={{ marginBottom: 10, background: '#fff', borderRadius: 8, padding: '0.75rem', border: `0.5px solid ${colors.border}` }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 600, marginBottom: enabledTiers[tier] ? 8 : 0, textTransform: 'capitalize' }}>
            <input type="checkbox" checked={enabledTiers[tier]} onChange={() => toggleTier(tier)} disabled={tier === 'basic'} />
            {tier} {tier === 'basic' && '(required)'}
          </label>
          {enabledTiers[tier] && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
              <div>
                <label style={{ fontSize: 11, color: colors.textMuted, display: 'block', marginBottom: 3 }}>{t('fl_price')}</label>
                <input style={inputStyle} type="number" step="0.01" placeholder="e.g. 10" value={packages[tier]?.price_pi || ''} onChange={updatePkg(tier, 'price_pi')} />
                {parseFloat(packages[tier]?.price_pi) > 0 && (
                  <p style={{ fontSize: 11, margin: '2px 0 6px' }}><PiPrice pi={parseFloat(packages[tier].price_pi)} /></p>
                )}
              </div>
              <div>
                <label style={{ fontSize: 11, color: colors.textMuted, display: 'block', marginBottom: 3 }}>{t('fl_delivery')}</label>
                <input style={inputStyle} type="number" placeholder="e.g. 3" value={packages[tier]?.delivery_days || ''} onChange={updatePkg(tier, 'delivery_days')} />
              </div>
              <div>
                <label style={{ fontSize: 11, color: colors.textMuted, display: 'block', marginBottom: 3 }}>{t('revisions')}</label>
                <input style={inputStyle} type="number" placeholder="e.g. 1" value={packages[tier]?.revisions || ''} onChange={updatePkg(tier, 'revisions')} />
              </div>
            </div>
          )}
        </div>
      ))}

      <button
        onClick={submit}
        disabled={submitting || !form.title}
        style={{ width: '100%', padding: '10px', fontSize: 14, fontWeight: 500, border: 'none', borderRadius: 6, background: colors.primary, color: '#fff', cursor: 'pointer', marginTop: 8 }}
      >
        {submitting ? t('fl_creating') : t('fl_publish')}
      </button>
    </div>
  );
}

function MyGigsPanel() {
  const [gigs, setGigs] = useState(null);
  const [boosting, setBoosting] = useState({});

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/gigs/mine/`, { headers: authHeaders() });
      if (res.ok) {
        const data = await safeJson(res);
        setGigs(data.gigs);
      }
    } catch (e) {}
  }, []);

  useEffect(() => { load(); }, [load]);

  const boostGig = async (gigId, title) => {
    if (!window.Pi) { alert('Open inside Pi Browser to pay with Pi.'); return; }
    const amount = 1;
    setBoosting((b) => ({ ...b, [gigId]: true }));
    try {
      await window.Pi.authenticate(['username', 'payments'], () => {});
      window.Pi.createPayment(
        { amount, memo: `Boost gig: ${title}`, metadata: { gig_id: gigId } },
        {
          onReadyForServerApproval: (paymentId) => {
            fetch(`${API_BASE}/gigs/${gigId}/boost/approve/`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ payment_id: paymentId }) });
          },
          onReadyForServerCompletion: (paymentId, txid) => {
            fetch(`${API_BASE}/gigs/${gigId}/boost/complete/`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ payment_id: paymentId, txid, amount, days: 7 }) })
              .then(() => { alert('Gig boosted for 7 days!'); setBoosting((b) => ({ ...b, [gigId]: false })); load(); })
              .catch(() => { alert('Boost activation failed.'); setBoosting((b) => ({ ...b, [gigId]: false })); });
          },
          onCancel: () => setBoosting((b) => ({ ...b, [gigId]: false })),
          onError: () => setBoosting((b) => ({ ...b, [gigId]: false })),
        }
      );
    } catch (e) {
      setBoosting((b) => ({ ...b, [gigId]: false }));
    }
  };

  return (
    <div>
      {gigs && gigs.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 10, marginBottom: '1.5rem' }}>
          {gigs.map((g) => (
            <div key={g.id} style={{ background: colors.surface, border: `0.5px solid ${colors.border}`, borderRadius: 10, padding: 8 }}>
              <div style={{ width: '100%', aspectRatio: '4/3', background: '#F1EFE8', borderRadius: 6, marginBottom: 6, overflow: 'hidden' }}>
                {g.cover_image && <img src={g.cover_image} alt={g.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
              </div>
              <p style={{ fontSize: 12, fontWeight: 500, margin: '0 0 2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{g.title}</p>
              <p style={{ fontSize: 11, color: colors.textMuted, margin: '0 0 6px' }}>
                {g.orders_count} orders · From <PiPrice pi={g.packages[0]?.price_pi || 0} />
              </p>
              <button
                onClick={() => boostGig(g.id, g.title)}
                disabled={boosting[g.id] || g.is_boosted}
                style={{
                  width: '100%', padding: '4px', fontSize: 11, fontWeight: 500, border: 'none', borderRadius: 4,
                  background: g.is_boosted ? '#FFF3C4' : colors.primaryBg, color: g.is_boosted ? '#8A6D00' : colors.primary,
                  cursor: g.is_boosted ? 'default' : 'pointer',
                }}
              >
                {g.is_boosted ? '📢 Boosted' : boosting[g.id] ? 'Processing...' : '📢 Boost (1 Pi / 7d)'}
              </button>
            </div>
          ))}
        </div>
      )}
      <CreateGigForm onGigCreated={load} />
    </div>
  );
}

function OrdersPanel() {
  const [view, setView] = useState('bought');
  // Bell se aane par: /freelance?order=12&chat=1
  const [focusOrderId] = useState(() => {
    try {
      const p = new URLSearchParams(window.location.search);
      return p.get('order') || '';
    } catch (e) { return ''; }
  });
  const [focusChat] = useState(() => {
    try {
      return new URLSearchParams(window.location.search).get('chat') === '1';
    } catch (e) { return false; }
  });
  const [unread, setUnread] = useState({});
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const endpoint = view === 'bought' ? 'orders/bought/' : 'orders/sold/';
      fetch(`${API_BASE}/orders/unread/`, { headers: authHeaders() })
        .then((r) => (r.ok ? r.json() : null))
        .then((j) => { if (j && j.unread) setUnread(j.unread); })
        .catch(() => {});
      const res = await fetch(`${API_BASE}/${endpoint}`, { headers: authHeaders() });
      if (res.ok) {
        const data = await safeJson(res);
        setOrders(data.orders);
      }
    } catch (e) {} finally {
      setLoading(false);
    }
  }, [view]);

  useEffect(() => { load(); }, [load]);

  const [deliverForms, setDeliverForms] = useState({});

  const submitDelivery = async (orderId) => {
    const form = deliverForms[orderId] || {};
    try {
      const res = await fetch(`${API_BASE}/orders/${orderId}/deliver/`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ delivery_note: form.note || '', delivery_link: form.link || '' }),
      });
      const data = await safeJson(res);
      if (!res.ok) throw new Error(data.error || 'Could not deliver.');
      alert('Delivered!');
      load();
    } catch (e) {
      alert(e.message);
    }
  };

  const acceptOrder = async (orderId) => {
    try {
      const res = await fetch(`${API_BASE}/orders/${orderId}/accept/`, { method: 'POST', headers: authHeaders() });
      const data = await safeJson(res);
      if (!res.ok) throw new Error(data.error || 'Could not accept.');
      alert(`Accepted! ${data.payout_pi} Pi released to the freelancer.`);
      load();
    } catch (e) {
      alert(e.message);
    }
  };

  const openDispute = async (orderId) => {
    const reason = prompt('What went wrong? (an admin will review this)');
    if (reason === null) return;
    if (!window.confirm('Open a dispute? The payment stays on hold until an admin decides.')) return;
    try {
      const res = await fetch(`${API_BASE}/orders/${orderId}/dispute/`, {
        method: 'POST', headers: authHeaders(), body: JSON.stringify({ reason }),
      });
      const data = await safeJson(res);
      if (!res.ok) throw new Error(data.error || 'Could not open dispute.');
      alert('Dispute opened. An admin will review it.');
      load();
    } catch (e) {
      alert(e.message);
    }
  };

  const requestRevision = async (orderId) => {
    const notes = prompt('What needs to change?');
    if (notes === null) return;
    try {
      const res = await fetch(`${API_BASE}/orders/${orderId}/request-revision/`, {
        method: 'POST', headers: authHeaders(), body: JSON.stringify({ notes }),
      });
      const data = await safeJson(res);
      if (!res.ok) throw new Error(data.error || 'Could not request revision.');
      alert('Revision requested.');
      load();
    } catch (e) {
      alert(e.message);
    }
  };

  const leaveReview = async (orderId) => {
    const rating = parseInt(prompt('Rate 1-5:', '5'), 10);
    if (!rating) return;
    const comment = prompt('Comment (optional):', '') || '';
    try {
      const res = await fetch(`${API_BASE}/orders/${orderId}/review/`, {
        method: 'POST', headers: authHeaders(), body: JSON.stringify({ rating, comment }),
      });
      const data = await safeJson(res);
      if (!res.ok) throw new Error(data.error || 'Could not submit review.');
      alert('Thanks for your review!');
    } catch (e) {
      alert(e.message);
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        <button onClick={() => setView('bought')} style={{ padding: '4px 12px', fontSize: 12, borderRadius: 14, border: `0.5px solid ${colors.border}`, background: view === 'bought' ? colors.primaryBg : '#fff', color: view === 'bought' ? colors.primary : colors.textMuted, cursor: 'pointer' }}>
          As Client
        </button>
        <button onClick={() => setView('sold')} style={{ padding: '4px 12px', fontSize: 12, borderRadius: 14, border: `0.5px solid ${colors.border}`, background: view === 'sold' ? colors.primaryBg : '#fff', color: view === 'sold' ? colors.primary : colors.textMuted, cursor: 'pointer' }}>
          As Freelancer
        </button>
        <button onClick={() => setView('offers')} style={{ padding: '4px 12px', fontSize: 12, borderRadius: 14, border: `0.5px solid ${colors.border}`, background: view === 'offers' ? colors.primaryBg : '#fff', color: view === 'offers' ? colors.primary : colors.textMuted, cursor: 'pointer' }}>
          Offers
        </button>
        <button onClick={load} style={{ marginLeft: 'auto', fontSize: 12, padding: '4px 10px', border: `0.5px solid ${colors.border}`, borderRadius: 6, background: '#fff', cursor: 'pointer' }}>🔄</button>
      </div>

      {view === 'offers' ? (
        <CustomOffersTab />
      ) : loading ? (
        <p style={{ color: colors.textMuted, textAlign: 'center', padding: '2rem 0' }}>{t('loading')}</p>
      ) : orders.length === 0 ? (
        <p style={{ color: colors.textMuted, textAlign: 'center', padding: '2rem 0' }}>{t('no_orders_yet')}</p>
      ) : (
        orders.map((o) => (
          <div key={o.id} id={'order-' + o.id} style={{ background: colors.surface, border: `0.5px solid ${colors.border}`, borderRadius: 10, padding: '0.75rem', marginBottom: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
              <span style={{ fontSize: 13, fontWeight: 500 }}>{o.gig_title} · {o.tracking_code}</span>
              <span style={{ fontSize: 12, color: colors.textMuted, textTransform: 'capitalize' }}>{o.status}</span>
            </div>
            <p style={{ fontSize: 12, color: colors.textMuted, margin: '2px 0' }}>
              {view === 'bought' ? `Freelancer: @${o.seller_username}` : `Client: @${o.buyer_username}`} · {o.package_tier} · <PiPrice pi={o.price_pi} />
            </p>
            {/* order-conversation: brief aur jawab hamesha dono taraf */}
            {o.requirements && (
              <div style={{ margin: '6px 0', background: '#F1EFE8', padding: '6px 8px', borderRadius: 6 }}>
                <p style={{ fontSize: 10, color: colors.textMuted, margin: '0 0 2px', fontWeight: 600 }}>
                  {view === 'bought' ? 'Your brief' : 'Client brief'}
                </p>
                <p style={{ fontSize: 12, color: colors.text, margin: 0, whiteSpace: 'pre-wrap' }}>{o.requirements}</p>
              </div>
            )}

            {o.delivery_note && o.status !== 'delivered' && (
              <div style={{ margin: '6px 0', background: '#E1F5EE', padding: '6px 8px', borderRadius: 6 }}>
                <p style={{ fontSize: 10, color: colors.textMuted, margin: '0 0 2px', fontWeight: 600 }}>
                  {view === 'bought' ? 'Freelancer replied' : 'Your delivery note'}
                </p>
                <p style={{ fontSize: 12, color: colors.text, margin: 0, whiteSpace: 'pre-wrap' }}>{o.delivery_note}</p>
                {o.delivery_link && (
                  <a href={o.delivery_link} target="_blank" rel="noopener noreferrer"
                     style={{ fontSize: 11, color: colors.primary }}>
                    {t('fl_view_delivery')} &rarr;
                  </a>
                )}
              </div>
            )}

            {view === 'bought' && (o.status === 'paid' || o.status === 'pending') && (
              <div style={{ marginTop: 8 }}>
                <p style={{ fontSize: 12, color: colors.textMuted, margin: '0 0 2px' }}>
                  Sample or reference files for the freelancer:
                </p>
                <AttachmentBox orderId={o.id} canUpload kind="brief" chatWith={o.seller_username} />
              </div>
            )}

            {view === 'sold' && o.status === 'paid' && (
              <div style={{ marginTop: 8 }}>
                <textarea
                  placeholder={t('delivery_message')}
                  rows={2}
                  onChange={(e) => setDeliverForms((f) => ({ ...f, [o.id]: { ...(f[o.id] || {}), note: e.target.value } }))}
                  style={{ width: '100%', padding: '6px 8px', fontSize: 12, border: `0.5px solid ${colors.border}`, borderRadius: 6, marginBottom: 6, boxSizing: 'border-box' }}
                />
                <p style={{ fontSize: 10, color: colors.textMuted, margin: '0 0 4px' }}>
                  Link only for big files (video, PSD, ZIP) - for images just attach them below.
                </p>
                <input
                  placeholder={t('delivery_link')}
                  onChange={(e) => setDeliverForms((f) => ({ ...f, [o.id]: { ...(f[o.id] || {}), link: e.target.value } }))}
                  style={{ width: '100%', padding: '6px 8px', fontSize: 12, border: `0.5px solid ${colors.border}`, borderRadius: 6, marginBottom: 6, boxSizing: 'border-box' }}
                />
                <AttachmentBox orderId={o.id} canUpload kind="delivery" chatWith={o.buyer_username} />
                <button onClick={() => submitDelivery(o.id)} style={{ fontSize: 12, padding: '6px 12px', border: 'none', borderRadius: 6, background: colors.primary, color: '#fff', cursor: 'pointer' }}>
                  Submit delivery
                </button>
                <button onClick={() => openDispute(o.id)} style={{ marginLeft: 8, fontSize: 12, padding: '6px 12px', border: `0.5px solid ${colors.border}`, borderRadius: 6, background: '#fff', color: colors.textMuted, cursor: 'pointer' }}>
                  ⚠️ Dispute
                </button>
              </div>
            )}

            {view === 'bought' && o.status === 'delivered' && (
              <div style={{ marginTop: 8 }}>
                <p style={{ fontSize: 12, margin: '0 0 4px' }}>{o.delivery_note}</p>
                <p style={{ fontSize: 10, color: colors.textMuted, margin: '0 0 4px' }}>
                  Auto-completes 7 days after delivery if you do not respond.
                </p>
                <AttachmentBox orderId={o.id} canUpload kind="revision" chatWith={o.seller_username} />
                {o.delivery_link && <a href={o.delivery_link} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, color: colors.primary }}>{t('fl_view_delivery')} →</a>}
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <button onClick={() => acceptOrder(o.id)} style={{ fontSize: 12, padding: '6px 12px', border: 'none', borderRadius: 6, background: colors.primary, color: '#fff', cursor: 'pointer' }}>
                    ✅ Accept
                  </button>
                  {o.revisions_used < o.revisions_allowed && (
                    <button onClick={() => requestRevision(o.id)} style={{ fontSize: 12, padding: '6px 12px', border: `0.5px solid ${colors.border}`, borderRadius: 6, background: '#fff', cursor: 'pointer' }}>
                      🔁 Request revision ({o.revisions_allowed - o.revisions_used} left)
                    </button>
                  )}
                  <button onClick={() => openDispute(o.id)} style={{ fontSize: 12, padding: '6px 12px', border: `0.5px solid ${colors.danger}`, borderRadius: 6, background: '#fff', color: colors.danger, cursor: 'pointer' }}>
                    ⚠️ Dispute
                  </button>
                </div>
              </div>
            )}

            {/* completed-delivery: accept ke baad bhi kaam nazar aana chahiye */}
            {(o.status === 'completed' || o.status === 'refunded') && (
              <div style={{ marginTop: 8, background: '#F7F5F0', borderRadius: 8, padding: '8px 10px' }}>
                <p style={{ fontSize: 11, color: colors.textMuted, margin: '0 0 4px', fontWeight: 600 }}>
                  Delivered work
                </p>
                {o.delivery_note && (
                  <p style={{ fontSize: 12, margin: '0 0 4px' }}>{o.delivery_note}</p>
                )}
                {o.delivery_link && (
                  <a href={o.delivery_link} target="_blank" rel="noopener noreferrer"
                     style={{ fontSize: 12, color: colors.primary }}>
                    {t('fl_view_delivery')} &rarr;
                  </a>
                )}
                <AttachmentBox
                  orderId={o.id}
                  kind="delivery"
                  chatWith={view === 'bought' ? o.seller_username : o.buyer_username}
                />
              </div>
            )}

            {view === 'bought' && o.status === 'completed' && (
              <button onClick={() => leaveReview(o.id)} style={{ marginTop: 6, fontSize: 11, padding: '4px 10px', border: `0.5px solid ${colors.border}`, borderRadius: 10, background: '#fff', cursor: 'pointer' }}>
                ⭐ Leave a review
              </button>
            )}

            {view === 'sold' && o.status === 'completed' && (
              <p style={{ fontSize: 12, color: colors.primary, marginTop: 6 }}>✅ Completed - <PiPrice pi={o.net_payout_pi} /> added to your wallet</p>
            )}
          
            {/* chat-every-order: har order par, har halat mein */}
            <OrderDeadline orderId={o.id} status={o.status} onChanged={load} />
            <OrderCancel orderId={o.id} status={o.status} onChanged={load} />
            {view === 'bought' && <PayOrderButton order={o} onPaid={load} />}
            <Milestones orderId={o.id} />
            <OrderChat orderId={o.id} unread={unread[String(o.id)] || 0} autoOpen={focusChat && String(o.id) === focusOrderId} />
          </div>
        ))
      )}
    </div>
  );
}

function WalletPanel() {
  const [balance, setBalance] = useState(null);
  const [ledger, setLedger] = useState([]);
  const [showLedger, setShowLedger] = useState(false);
  const [amount, setAmount] = useState('');
  const [walletAddress, setWalletAddress] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/wallet/balance/`, { headers: authHeaders() });
      if (res.ok) setBalance(await safeJson(res));
    } catch (e) {}
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleLedger = async () => {
    if (!showLedger) {
      try {
        const res = await fetch(`${API_BASE}/wallet/ledger/`, { headers: authHeaders() });
        if (res.ok) setLedger((await safeJson(res)).entries);
      } catch (e) {}
    }
    setShowLedger((s) => !s);
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/wallet/withdraw/`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ amount: parseFloat(amount), pi_wallet_address: walletAddress }),
      });
      const data = await safeJson(res);
      if (!res.ok) throw new Error(data.error || 'Could not submit request.');
      alert('Withdrawal request submitted.');
      setAmount(''); setWalletAddress('');
      load();
    } catch (e) {
      alert(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (balance === null) return <p style={{ color: colors.textMuted, textAlign: 'center', padding: '2rem 0' }}>{t('loading_wallet')}</p>;

  return (
    <div>
      <div style={{ background: colors.surface, border: `0.5px solid ${colors.border}`, borderRadius: 12, padding: '1rem', marginBottom: '1.5rem' }}>
        <p style={{ fontSize: 13, color: colors.textMuted, margin: '0 0 4px' }}>{t('available_to_withdraw')}</p>
        <p style={{ fontSize: 26, fontWeight: 600, margin: '0 0 4px', color: colors.primary }}><PiPrice pi={balance.available_balance_pi} /></p>
        <p style={{ fontSize: 12, color: colors.textMuted, margin: '0 0 8px' }}>Total earned (after 5% fee): <PiPrice pi={balance.total_earned_pi} /></p>
        <button onClick={toggleLedger} style={{ fontSize: 12, padding: '4px 10px', border: `0.5px solid ${colors.border}`, borderRadius: 6, background: '#fff', cursor: 'pointer' }}>
          {showLedger ? 'Hide' : 'View'} ledger
        </button>
        {showLedger && (
          <div style={{ marginTop: 10, borderTop: `0.5px solid ${colors.border}`, paddingTop: 10 }}>
            {ledger.length === 0 ? (
              <p style={{ fontSize: 12, color: colors.textMuted }}>{t('no_completed_orders')}</p>
            ) : (
              ledger.map((e) => (
                <div key={e.order_id} style={{ fontSize: 12, padding: '6px 0', borderBottom: `0.5px solid ${colors.border}` }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>{e.gig_title} · @{e.buyer}</span>
                    <span><PiPrice pi={e.net_pi} /> net</span>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      <div style={{ background: '#F1EFE8', border: `0.5px solid ${colors.border}`, borderRadius: 12, padding: '1.25rem' }}>
        <p style={{ fontWeight: 500, fontSize: 14, margin: '0 0 12px' }}>{t('request_withdrawal')}</p>
        <div style={{ display: 'grid', gap: 10, marginBottom: 10 }}>
          <input type="number" step="0.000001" placeholder={t('amount_pi')} value={amount} onChange={(e) => setAmount(e.target.value)} style={{ padding: '8px 10px', fontSize: 14, border: '0.5px solid #B4B2A9', borderRadius: 6 }} />
          <input type="text" placeholder={t('your_wallet_address')} value={walletAddress} onChange={(e) => setWalletAddress(e.target.value)} style={{ padding: '8px 10px', fontSize: 14, border: '0.5px solid #B4B2A9', borderRadius: 6 }} />
        </div>
        <button onClick={submit} disabled={submitting || !amount || !walletAddress} style={{ width: '100%', padding: '10px', fontSize: 14, fontWeight: 500, border: 'none', borderRadius: 6, background: colors.primary, color: '#fff', cursor: 'pointer' }}>
          {submitting ? 'Submitting...' : t('fl_req_wd')}
        </button>
      </div>
    </div>
  );
}

function FreelanceHelpModal({ onClose }) {
  const sectionStyle = { marginBottom: 20 };
  const headingStyle = { fontSize: 16, fontWeight: 600, margin: '0 0 8px' };
  const textStyle = { fontSize: 14, color: '#3A3935', lineHeight: 1.6, margin: '0 0 8px' };

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '20px 12px', overflowY: 'auto' }} onClick={onClose}>
      <div style={{ background: '#fff', borderRadius: 16, maxWidth: 640, width: '100%', padding: '1.5rem', marginTop: 20 }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h2 style={{ margin: 0, fontSize: 20 }}>{t('flh_title')}</h2>
          <button onClick={onClose} style={{ border: 'none', background: 'transparent', fontSize: 20, cursor: 'pointer', color: colors.textMuted }}>✕</button>
        </div>

        <div style={sectionStyle}>
          <p style={headingStyle}>{t('flh_what_q')}</p>
          <p style={textStyle}>{t('flh_what')}</p>
        </div>

        <div style={sectionStyle}>
          <p style={headingStyle}>{t('flh_two_q')}</p>
          <p style={textStyle}><b>Gigs</b>{t('flh_gigs_txt')}</p>
          <p style={textStyle}><b>{t('fl_job_board')}</b>{t('flh_jb_txt')}</p>
        </div>

        <div style={sectionStyle}>
          <p style={headingStyle}>{t('flh_hire_q')}</p>
          <p style={textStyle}>{t('flh_hire1')}</p>
          <p style={textStyle}>{t('flh_hire2')}</p>
          <p style={textStyle}>{t('flh_hire3')}</p>
          <p style={textStyle}>{t('flh_hire4')}</p>
          <p style={textStyle}>{t('flh_hire5')}</p>
        </div>

        <div style={sectionStyle}>
          <p style={headingStyle}>{t('flh_sell_q')}</p>
          <p style={textStyle}>{t('flh_sell1')}</p>
          <p style={textStyle}>{t('flh_sell2')}</p>
          <p style={textStyle}>{t('flh_sell3')}</p>
          <p style={textStyle}>{t('flh_sell4')}</p>
          <p style={textStyle}>{t('flh_sell5')}</p>
        </div>

        <div style={sectionStyle}>
          <p style={headingStyle}>{t('flh_fees_q')}</p>
          <p style={textStyle}>{t('flh_fees')}</p>
        </div>

        <div style={sectionStyle}>
          <p style={headingStyle}>{t('flh_faq_q')}</p>
          <p style={{ ...textStyle, fontWeight: 600, marginBottom: 2 }}>{t('flh_q1')}</p>
          <p style={textStyle}>{t('flh_a1')}</p>
          <p style={{ ...textStyle, fontWeight: 600, marginBottom: 2 }}>{t('flh_q2')}</p>
          <p style={textStyle}>{t('flh_a2')}</p>
          <p style={{ ...textStyle, fontWeight: 600, marginBottom: 2 }}>{t('flh_q3')}</p>
          <p style={textStyle}>{t('flh_a3')}</p>
          <p style={{ ...textStyle, fontWeight: 600, marginBottom: 2 }}>{t('flh_q4')}</p>
          <p style={textStyle}>{t('flh_a4')}</p>
          <p style={{ ...textStyle, fontWeight: 600, marginBottom: 2 }}>{t('flh_q5')}</p>
          <p style={textStyle}>{t('flh_a5')}</p>
        </div>

        <button onClick={onClose} style={{ width: '100%', padding: '10px', fontSize: 14, fontWeight: 500, border: 'none', borderRadius: 8, background: colors.primary, color: '#fff', cursor: 'pointer' }}>
          Got it
        </button>
      </div>
    </div>
  );
}

function JobBoardPanel() {
  const [subtab, setSubtab] = useState('browse');
  const [jobs, setJobs] = useState([]);
  const [openJobId, setOpenJobId] = useState(null);
  const [myJobs, setMyJobs] = useState([]);
  const [engagements, setEngagements] = useState({ as_client: [], as_freelancer: [] });
  const [showPostForm, setShowPostForm] = useState(false);
  const [postForm, setPostForm] = useState({ title: '', description: '', category: 'other', budget_min_pi: '', budget_max_pi: '' });

  const inputStyle = { width: '100%', padding: '8px 10px', fontSize: 13, border: '0.5px solid #B4B2A9', borderRadius: 6, boxSizing: 'border-box' };

  const loadJobs = useCallback(async () => {
    const res = await fetch(`${API_BASE}/jobs/`);
    if (res.ok) setJobs((await safeJson(res)).jobs);
  }, []);

  const loadMyJobs = useCallback(async () => {
    const res = await fetch(`${API_BASE}/jobs/mine/`, { headers: authHeaders() });
    if (res.ok) setMyJobs((await safeJson(res)).jobs);
  }, []);

  const loadEngagements = useCallback(async () => {
    const res = await fetch(`${API_BASE}/engagements/mine/`, { headers: authHeaders() });
    if (res.ok) setEngagements(await safeJson(res));
  }, []);

  useEffect(() => { loadJobs(); }, [loadJobs]);
  useEffect(() => { loadMyJobs(); }, [loadMyJobs]);
  useEffect(() => { loadEngagements(); }, [loadEngagements]);

  const postJob = async () => {
    try {
      const res = await fetch(`${API_BASE}/jobs/create/`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(postForm) });
      const data = await safeJson(res);
      if (!res.ok) throw new Error(data.error || 'Could not post job.');
      alert('Job posted!');
      setShowPostForm(false);
      setPostForm({ title: '', description: '', category: 'other', budget_min_pi: '', budget_max_pi: '' });
      loadJobs();
      loadMyJobs();
    } catch (e) { alert(networkMessage(e)); }
  };

  const tabBtn = (t, label) => (
    <button onClick={() => setSubtab(t)} style={{ padding: '5px 12px', fontSize: 12, borderRadius: 14, border: `0.5px solid ${colors.border}`, background: subtab === t ? colors.primaryBg : '#fff', color: subtab === t ? colors.primary : colors.textMuted, cursor: 'pointer' }}>{label}</button>
  );

  return (
    <div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 14, flexWrap: 'wrap' }}>
        {tabBtn('browse', 'Browse Jobs')}
        {tabBtn('post', 'Post a Job')}
        {tabBtn('myjobs', 'My Posted Jobs')}
        {tabBtn('engagements', 'My Engagements')}
        <a href="/careers" style={{ fontSize: 12, color: '#4f46e5', alignSelf: 'center', marginLeft: 'auto', textDecoration: 'none' }}>🏢 Full-time jobs →</a>
      </div>

      {subtab === 'browse' && (
        jobs.length === 0 ? <p style={{ color: colors.textMuted, textAlign: 'center', padding: '2rem 0' }}>{t('no_open_jobs')}</p> : (
          jobs.map((j) => (
            <div key={j.id} onClick={() => setOpenJobId(j.id)} style={{ background: colors.surface, border: `0.5px solid ${colors.border}`, borderRadius: 10, padding: '0.75rem', marginBottom: 8, cursor: 'pointer' }}>
              <p style={{ fontSize: 14, fontWeight: 500, margin: '0 0 4px' }}>{j.title}</p>
              <p style={{ fontSize: 12, color: colors.textMuted, margin: 0 }}>Budget: {j.budget_min_pi}-<PiPrice pi={j.budget_max_pi} /> · {j.proposals_count} proposals · @{j.client_username}</p>
            </div>
          ))
        )
      )}

      {subtab === 'post' && (
        <div style={{ background: '#F1EFE8', borderRadius: 10, padding: '0.75rem' }}>
          <div style={{ display: 'grid', gap: 8 }}>
            <input style={inputStyle} placeholder={t('job_title')} value={postForm.title} onChange={(e) => setPostForm((f) => ({ ...f, title: e.target.value }))} />
            <textarea style={{ ...inputStyle, resize: 'vertical' }} rows={4} placeholder={t('describe_job')} value={postForm.description} onChange={(e) => setPostForm((f) => ({ ...f, description: e.target.value }))} />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <input style={inputStyle} type="number" placeholder={t('min_budget')} value={postForm.budget_min_pi} onChange={(e) => setPostForm((f) => ({ ...f, budget_min_pi: e.target.value }))} />
              <input style={inputStyle} type="number" placeholder={t('max_budget')} value={postForm.budget_max_pi} onChange={(e) => setPostForm((f) => ({ ...f, budget_max_pi: e.target.value }))} />
            </div>
          </div>
          <button onClick={postJob} disabled={!postForm.title} style={{ width: '100%', marginTop: 8, padding: '10px', fontSize: 13, border: 'none', borderRadius: 6, background: colors.primary, color: '#fff', cursor: 'pointer' }}>{t('post_job')}</button>
        </div>
      )}

      {subtab === 'myjobs' && (
        myJobs.length === 0 ? <p style={{ color: colors.textMuted, textAlign: 'center', padding: '2rem 0' }}>{t('no_jobs_posted')}</p> : (
          myJobs.map((j) => (
            <div key={j.id} onClick={() => setOpenJobId(j.id)} style={{ background: colors.surface, border: `0.5px solid ${colors.border}`, borderRadius: 10, padding: '0.75rem', marginBottom: 8, cursor: 'pointer' }}>
              <p style={{ fontSize: 14, fontWeight: 500, margin: '0 0 4px' }}>{j.title}</p>
              <p style={{ fontSize: 12, color: colors.textMuted, margin: 0, textTransform: 'capitalize' }}>{j.status} · {j.proposals_count} proposals</p>
            </div>
          ))
        )
      )}

      {subtab === 'engagements' && (
        <div>
          <p style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>{t('as_client')}</p>
          {engagements.as_client.length === 0 ? <p style={{ fontSize: 12, color: colors.textMuted, marginBottom: 16 }}>{t('none_yet')}</p> :
            engagements.as_client.map((e) => <EngagementRow key={`c${e.id}`} e={e} role="client" onChanged={loadEngagements} />)
          }
          <p style={{ fontSize: 13, fontWeight: 600, margin: '16px 0 8px' }}>{t('as_freelancer')}</p>
          {engagements.as_freelancer.length === 0 ? <p style={{ fontSize: 12, color: colors.textMuted }}>{t('none_yet')}</p> :
            engagements.as_freelancer.map((e) => <EngagementRow key={`f${e.id}`} e={e} role="freelancer" onChanged={loadEngagements} />)
          }
        </div>
      )}

      {openJobId && <JobDetailModal jobId={openJobId} onClose={() => setOpenJobId(null)} onChanged={() => { loadJobs(); loadMyJobs(); loadEngagements(); }} />}
    </div>
  );
}

function EngagementRow({ e, role, onChanged }) {
  const payNow = async () => {
    if (!window.Pi) { alert('Open inside Pi Browser to pay with Pi.'); return; }
    try {
      await window.Pi.authenticate(['username', 'payments'], () => {});
      window.Pi.createPayment(
        { amount: e.price_pi, memo: `Job: ${e.job_title}`, metadata: { engagement_id: e.id } },
        {
          onReadyForServerApproval: (paymentId) => fetch(`${API_BASE}/engagements/${e.id}/approve/`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ payment_id: paymentId }) }),
          onReadyForServerCompletion: (paymentId, txid) => {
            fetch(`${API_BASE}/engagements/${e.id}/complete/`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ payment_id: paymentId, txid }) })
              .then(() => { alert('Paid! Work can now begin.'); onChanged(); })
              .catch(() => alert('Payment reached Pi but activation failed.'));
          },
          onCancel: () => {}, onError: () => alert(t('fl_pay_err')),
        }
      );
    } catch (err) {}
  };

  const deliver = async () => {
    const delivery_link = prompt('Link to your delivered work:');
    if (!delivery_link) return;
    const delivery_note = prompt('Note for the client (optional):', '') || '';
    await fetch(`${API_BASE}/engagements/${e.id}/deliver/`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ delivery_link, delivery_note }) });
    onChanged();
  };

  const accept = async () => {
    await fetch(`${API_BASE}/engagements/${e.id}/accept/`, { method: 'POST', headers: authHeaders() });
    onChanged();
  };

  const leaveReview = async () => {
    const rating = parseInt(prompt('Rate 1-5:', '5'), 10);
    if (!rating) return;
    const comment = prompt('Comment (optional):', '') || '';
    await fetch(`${API_BASE}/engagements/${e.id}/review/`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ rating, comment }) });
    alert('Thanks for your review!');
  };

  return (
    <div style={{ background: colors.surface, border: `0.5px solid ${colors.border}`, borderRadius: 10, padding: '0.75rem', marginBottom: 8 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <span style={{ fontSize: 13, fontWeight: 500 }}>{e.job_title} · @{e.other_username}</span>
        <span style={{ fontSize: 12, color: colors.textMuted, textTransform: 'capitalize' }}>{e.status}</span>
      </div>
      <p style={{ fontSize: 12, color: colors.textMuted, margin: '2px 0' }}>
        <PiPrice pi={e.price_pi} /> {e.discount_percent_applied > 0 && `(${e.discount_percent_applied}% repeat-client discount applied)`} · {e.tracking_code}
      </p>
      {e.delivery_link && <p style={{ fontSize: 12, margin: '4px 0' }}>📎 <a href={e.delivery_link} target="_blank" rel="noopener noreferrer">{t('delivery_link_text')}</a> {e.delivery_note && `- ${e.delivery_note}`}</p>}
      <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
        {role === 'client' && e.status === 'pending' && <button onClick={payNow} style={{ fontSize: 11, padding: '4px 10px', border: 'none', borderRadius: 8, background: colors.primary, color: '#fff', cursor: 'pointer' }}>{t('pay_now')}</button>}
        {role === 'freelancer' && e.status === 'paid' && <button onClick={deliver} style={{ fontSize: 11, padding: '4px 10px', border: 'none', borderRadius: 8, background: colors.primary, color: '#fff', cursor: 'pointer' }}>{t('deliver_work')}</button>}
        {role === 'client' && e.status === 'delivered' && <button onClick={accept} style={{ fontSize: 11, padding: '4px 10px', border: 'none', borderRadius: 8, background: colors.primary, color: '#fff', cursor: 'pointer' }}>{t('accept_delivery')}</button>}
        {role === 'client' && e.status === 'completed' && <button onClick={leaveReview} style={{ fontSize: 11, padding: '4px 10px', border: `0.5px solid ${colors.border}`, borderRadius: 8, background: '#fff', cursor: 'pointer' }}>⭐ {t('fl_review')}</button>}
      </div>
    </div>
  );
}

function JobDetailModal({ jobId, onClose, onChanged }) {
  const [job, setJob] = useState(null);
  const [proposals, setProposals] = useState(null);
  const [showProposeForm, setShowProposeForm] = useState(false);
  const [form, setForm] = useState({ cover_letter: '', proposed_price_pi: '', proposed_delivery_days: 7 });

  const load = useCallback(async () => {
    const res = await fetch(`${API_BASE}/jobs/${jobId}/`, { headers: getAuthToken() ? authHeaders() : {} });
    if (res.ok) setJob(await safeJson(res));
  }, [jobId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (job?.is_own_job) {
      fetch(`${API_BASE}/jobs/${jobId}/proposals/`, { headers: authHeaders() }).then((r) => r.json()).then((d) => setProposals(d.proposals)).catch(() => {});
    }
  }, [job, jobId]);

  const submitProposal = async () => {
    try {
      const res = await fetch(`${API_BASE}/jobs/${jobId}/propose/`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(form) });
      const data = await safeJson(res);
      if (!res.ok) throw new Error(data.error || 'Could not submit proposal.');
      alert('Proposal submitted!');
      onClose();
      onChanged();
    } catch (e) { alert(networkMessage(e)); }
  };

  const acceptProposal = async (proposalId) => {
    try {
      const res = await fetch(`${API_BASE}/proposals/${proposalId}/accept/`, { method: 'POST', headers: authHeaders() });
      const data = await safeJson(res);
      if (!res.ok) throw new Error(data.error || 'Could not accept.');
      alert(`Proposal accepted! ${data.discount_percent > 0 ? `${data.discount_percent}% repeat-client discount applied.` : ''} Check "My Engagements" to pay.`);
      onClose();
      onChanged();
    } catch (e) { alert(networkMessage(e)); }
  };

  const inputStyle = { width: '100%', padding: '8px 10px', fontSize: 13, border: '0.5px solid #B4B2A9', borderRadius: 6, boxSizing: 'border-box' };

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '20px 12px', overflowY: 'auto' }} onClick={onClose}>
      <div style={{ background: '#fff', borderRadius: 16, maxWidth: 560, width: '100%', padding: '1.5rem', marginTop: 20 }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ border: 'none', background: 'transparent', fontSize: 20, cursor: 'pointer', color: colors.textMuted }}>✕</button>
        </div>
        {!job ? <p style={{ textAlign: 'center', color: colors.textMuted }}>{t('loading')}</p> : (
          <>
            <h2 style={{ fontSize: 18, margin: '0 0 4px' }}>{job.title}</h2>
            <p style={{ fontSize: 13, color: colors.textMuted, margin: '0 0 12px' }}>Budget: {job.budget_min_pi}-{job.budget_max_pi} Pi · @{job.client_username}</p>
            <p style={{ fontSize: 14, lineHeight: 1.5, marginBottom: 16 }}>{job.description}</p>

            {job.is_own_job && proposals && (
              <div>
                <p style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Proposals ({proposals.length})</p>
                {proposals.length === 0 ? <p style={{ fontSize: 12, color: colors.textMuted }}>{t('no_proposals_yet')}</p> :
                  proposals.map((p) => (
                    <div key={p.id} style={{ background: '#F1EFE8', borderRadius: 8, padding: '0.6rem 0.75rem', marginBottom: 6 }}>
                      <p style={{ fontSize: 13, fontWeight: 500, margin: '0 0 2px' }}>
                        @{p.freelancer_username} {p.is_top_rated && '🏆 Top Rated'} {p.avg_rating > 0 && `⭐ ${p.avg_rating}`}
                      </p>
                      <p style={{ fontSize: 12, margin: '0 0 4px' }}>{p.proposed_price_pi} Pi · {p.proposed_delivery_days} days delivery</p>
                      <p style={{ fontSize: 12, color: colors.textMuted, margin: '0 0 6px' }}>{p.cover_letter}</p>
                      {p.status === 'pending' && <button onClick={() => acceptProposal(p.id)} style={{ fontSize: 11, padding: '4px 12px', border: 'none', borderRadius: 8, background: colors.primary, color: '#fff', cursor: 'pointer' }}>{t('accept_proposal')}</button>}
                      {p.status !== 'pending' && <span style={{ fontSize: 11, textTransform: 'capitalize', color: colors.textMuted }}>{p.status}</span>}
                    </div>
                  ))
                }
              </div>
            )}

            {!job.is_own_job && !job.my_proposal && job.status === 'open' && (
              showProposeForm ? (
                <div style={{ background: '#F1EFE8', borderRadius: 8, padding: '0.75rem' }}>
                  <div style={{ display: 'grid', gap: 8 }}>
                    <textarea style={{ ...inputStyle, resize: 'vertical' }} rows={3} placeholder={t('cover_letter_placeholder')} value={form.cover_letter} onChange={(e) => setForm((f) => ({ ...f, cover_letter: e.target.value }))} />
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                      <input style={inputStyle} type="number" placeholder={t('your_price')} value={form.proposed_price_pi} onChange={(e) => setForm((f) => ({ ...f, proposed_price_pi: e.target.value }))} />
                      <input style={inputStyle} type="number" placeholder={t('delivery_days')} value={form.proposed_delivery_days} onChange={(e) => setForm((f) => ({ ...f, proposed_delivery_days: e.target.value }))} />
                    </div>
                  </div>
                  <button onClick={submitProposal} style={{ width: '100%', marginTop: 8, padding: '10px', fontSize: 13, border: 'none', borderRadius: 6, background: colors.primary, color: '#fff', cursor: 'pointer' }}>{t('submit_proposal')}</button>
                </div>
              ) : (
                <button onClick={() => setShowProposeForm(true)} style={{ width: '100%', padding: '12px', fontSize: 14, fontWeight: 500, border: 'none', borderRadius: 8, background: colors.primary, color: '#fff', cursor: 'pointer' }}>{t('submit_a_proposal')}</button>
              )
            )}

            {job.my_proposal && (
              <p style={{ fontSize: 13, color: colors.primary, textAlign: 'center', padding: 10, background: colors.primaryBg, borderRadius: 8 }}>
                ✅ You already proposed {job.my_proposal.proposed_price_pi} Pi - status: {job.my_proposal.status}
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default function FreelanceMarketplace() {
  const [tab, setTab] = useState('browse');
  const [gigs, setGigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [sortBy, setSortBy] = useState('');
  const [flt, setFlt] = useState({ max_price: '', max_days: '', min_rating: '', min_on_time: '' });
  const [selectedCategory, setSelectedCategory] = useState('');
  const [categories, setCategories] = useState([]);
  const [personalized, setPersonalized] = useState(false);
  const [openGigSlug, setOpenGigSlug] = useState(null);
  const [showHelp, setShowHelp] = useState(false);
  const [piRate, setPiRate] = useState(null);

  const loadGigs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (selectedCategory) params.set('category', selectedCategory);
      if (sortBy) params.set('sort', sortBy);
      Object.entries(flt).forEach(([k, v]) => { if (v) params.set(k, v); });
      const res = await fetch(`${API_BASE}/gigs/?${params.toString()}`, { headers: getAuthToken() ? authHeaders() : {} });
      const data = await safeJson(res);
      setGigs(data.gigs || []);
      setPersonalized(!!data.personalized);
    } catch (e) {} finally {
      setLoading(false);
    }
  }, [search, selectedCategory, sortBy, flt]);

  const loadCategories = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/gigs/categories/`);
      if (res.ok) setCategories((await safeJson(res)).categories);
    } catch (e) {}
  }, []);

  useEffect(() => { loadGigs(); }, [loadGigs]);
  useEffect(() => { loadCategories(); }, [loadCategories]);
  useEffect(() => {
    fetch('https://api.coingecko.com/api/v3/simple/price?ids=pi-network-iou&vs_currencies=usd')
      .then((r) => r.json())
      .then((data) => { if (data['pi-network-iou']?.usd) setPiRate(data['pi-network-iou'].usd); })
      .catch(() => {});
  }, []);

  const tabStyle = (t) => ({
    padding: '8px 14px', fontSize: 13, fontWeight: 500, border: 'none',
    borderBottom: tab === t ? `2px solid ${colors.primary}` : '2px solid transparent',
    background: 'transparent', color: tab === t ? colors.primary : colors.textMuted, cursor: 'pointer',
    flexShrink: 0, whiteSpace: 'nowrap',
  });

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '1.5rem', fontFamily: 'sans-serif', background: colors.bg, minHeight: '100vh' }}>
      {showHelp && <FreelanceHelpModal onClose={() => setShowHelp(false)} />}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1 style={{ fontSize: 22, margin: '0 0 4px' }}>💼 {t('fl_app')}</h1>
          <p style={{ fontSize: 13, color: colors.textMuted, margin: '0 0 1rem' }}>{t('freelance_tagline')}</p>
        </div>
        <button
          onClick={() => setShowHelp(true)}
          style={{ padding: '6px 14px', fontSize: 13, fontWeight: 500, border: `0.5px solid ${colors.border}`, borderRadius: 20, background: '#fff', cursor: 'pointer', flexShrink: 0 }}
        >
          ❓ How it works
        </button>
      </div>

      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', borderBottom: `0.5px solid ${colors.border}`, marginBottom: '1.25rem' }}>
        <button style={tabStyle('browse')} onClick={() => setTab('browse')}>{t('browse')}</button>
        <button style={tabStyle('gigs')} onClick={() => setTab('gigs')}>{t('my_gigs')}</button>
        <button style={tabStyle('orders')} onClick={() => setTab('orders')}>{t('orders')}</button>
        <button style={tabStyle('wallet')} onClick={() => setTab('wallet')}>{t('wallet')}</button>
        <button style={tabStyle('jobboard')} onClick={() => setTab('jobboard')}>💼 {t('fl_job_board')}</button>
      </div>

      {tab === 'browse' && (
        <>
          <div style={{ display: 'flex', gap: 8, marginBottom: '0.75rem' }}>
            <input
              placeholder={t('fl_search_ph')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ flex: 1, padding: '10px 14px', fontSize: 14, border: `0.5px solid #B4B2A9`, borderRadius: 20, boxSizing: 'border-box' }}
            />
            <button onClick={loadGigs} style={{ padding: '0 14px', fontSize: 13, border: `0.5px solid ${colors.border}`, borderRadius: 20, background: '#fff', cursor: 'pointer' }}>🔄</button>
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', margin: '0 0 8px' }}>
            <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}
              style={{ fontSize: 12, padding: '5px 9px', border: `0.5px solid ${colors.border}`, borderRadius: 7, background: '#fff' }}>
              <option value="">Recommended</option>
              <option value="rating">Best rated</option>
              <option value="fastest">Fastest delivery</option>
              <option value="cheapest">Lowest price</option>
              <option value="expensive">Highest price</option>
              <option value="popular">Most orders</option>
              <option value="on_time">Most reliable</option>
            </select>
            <button onClick={() => setShowFilters(!showFilters)}
              style={{ fontSize: 12, padding: '5px 11px', border: `0.5px solid ${showFilters ? colors.primary : colors.border}`, borderRadius: 7, background: showFilters ? colors.primaryBg : '#fff', color: showFilters ? colors.primary : colors.textMuted, cursor: 'pointer' }}>
              Filters
            </button>
            {(flt.max_price || flt.max_days || flt.min_rating || flt.min_on_time) && (
              <button onClick={() => setFlt({ max_price: '', max_days: '', min_rating: '', min_on_time: '' })}
                style={{ fontSize: 12, padding: '5px 11px', border: `0.5px solid ${colors.danger}`, borderRadius: 7, background: '#fff', color: colors.danger, cursor: 'pointer' }}>
                Clear
              </button>
            )}
          </div>

          {showFilters && (
            <div style={{ background: colors.surface, borderRadius: 9, padding: 10, marginBottom: 10, display: 'flex', gap: 7, flexWrap: 'wrap' }}>
              <input type="number" value={flt.max_price}
                onChange={(e) => setFlt({ ...flt, max_price: e.target.value })}
                placeholder="Max price (Pi)"
                style={{ flex: '1 1 110px', padding: '6px 9px', fontSize: 12, border: `0.5px solid ${colors.border}`, borderRadius: 6 }} />
              <select value={flt.max_days} onChange={(e) => setFlt({ ...flt, max_days: e.target.value })}
                style={{ flex: '1 1 110px', padding: '6px 9px', fontSize: 12, border: `0.5px solid ${colors.border}`, borderRadius: 6, background: '#fff' }}>
                <option value="">Any delivery time</option>
                <option value="1">Within 24 hours</option>
                <option value="3">Up to 3 days</option>
                <option value="7">Up to 7 days</option>
                <option value="14">Up to 14 days</option>
              </select>
              <select value={flt.min_rating} onChange={(e) => setFlt({ ...flt, min_rating: e.target.value })}
                style={{ flex: '1 1 110px', padding: '6px 9px', fontSize: 12, border: `0.5px solid ${colors.border}`, borderRadius: 6, background: '#fff' }}>
                <option value="">Any rating</option>
                <option value="4">4 stars and up</option>
                <option value="4.5">4.5 stars and up</option>
              </select>
              <select value={flt.min_on_time} onChange={(e) => setFlt({ ...flt, min_on_time: e.target.value })}
                style={{ flex: '1 1 110px', padding: '6px 9px', fontSize: 12, border: `0.5px solid ${colors.border}`, borderRadius: 6, background: '#fff' }}>
                <option value="">Any reliability</option>
                <option value="80">80%+ on time</option>
                <option value="95">95%+ on time</option>
              </select>
            </div>
          )}

          {categories.length > 0 && (
            <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 8, marginBottom: '0.5rem' }}>
              <button onClick={() => setSelectedCategory('')} style={{ flexShrink: 0, padding: '5px 12px', fontSize: 12, borderRadius: 14, whiteSpace: 'nowrap', border: `0.5px solid ${colors.border}`, cursor: 'pointer', background: selectedCategory === '' ? colors.primary : '#fff', color: selectedCategory === '' ? '#fff' : colors.textMuted }}>
                All
              </button>
              {categories.map((c) => (
                <button key={c.name} onClick={() => setSelectedCategory(c.name)} style={{ flexShrink: 0, padding: '5px 12px', fontSize: 12, borderRadius: 14, whiteSpace: 'nowrap', border: `0.5px solid ${colors.border}`, cursor: 'pointer', background: selectedCategory === c.name ? colors.primary : '#fff', color: selectedCategory === c.name ? '#fff' : colors.textMuted }}>
                  {c.name} ({c.count})
                </button>
              ))}
            </div>
          )}

          {personalized && (
            <p style={{ fontSize: 12, color: colors.primary, margin: '0 0 0.75rem' }}>✨ {t('fl_sorted')}</p>
          )}

          {loading ? (
            <p style={{ color: colors.textMuted, textAlign: 'center', padding: '2rem 0' }}>{t('loading_gigs')}</p>
          ) : gigs.length === 0 ? (
            <p style={{ color: colors.textMuted, textAlign: 'center', padding: '2rem 0' }}>{t('no_gigs_found')}</p>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 12 }}>
              {gigs.map((g) => <GigCard key={g.id} gig={g} onOpen={setOpenGigSlug} piRate={piRate} />)}
            </div>
          )}

          {openGigSlug && (
            <GigDetailModal slug={openGigSlug} onClose={() => setOpenGigSlug(null)} onOrdered={() => setOpenGigSlug(null)} piRate={piRate} />
          )}
        </>
      )}

      {tab === 'gigs' && <MyGigsPanel />}
      {tab === 'orders' && <OrdersPanel />}
      {tab === 'wallet' && <WalletPanel />}
      {tab === 'jobboard' && <JobBoardPanel />}
    </div>
  );
}


// ==================== ORDER ATTACHMENTS (sample images, R2) ====================
function AttachmentBox({ orderId, canUpload, kind, chatWith }) {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [attachNote, setAttachNote] = useState('');

  const loadFiles = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/orders/${orderId}/attachments/`, { headers: authHeaders() });
      if (res.ok) {
        const d = await safeJson(res);
        setItems(d.attachments || []);
      }
    } catch (e) {}
  }, [orderId]);

  useEffect(() => { loadFiles(); }, [loadFiles]);

  const pickFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setBusy(true);
    setErr('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('kind', kind || 'delivery');
      fd.append('note', attachNote);
      const tk = getAuthToken();
      // NOTE: FormData ke sath Content-Type set NAHI karna - browser khud boundary lagata hai
      const res = await fetch(`${API_BASE}/orders/${orderId}/attachments/upload/`, {
        method: 'POST',
        headers: tk ? { Authorization: `Token ${tk}` } : {},
        body: fd,
      });
      const d = await safeJson(res);
      if (!res.ok) throw new Error(d.error || 'Upload failed.');
      setAttachNote('');
      loadFiles();
    } catch (ex) {
      setErr(ex.message);
    }
    setBusy(false);
    e.target.value = '';
  };

  const removeFile = async (id) => {
    try {
      await fetch(`${API_BASE}/attachments/${id}/delete/`, { method: 'DELETE', headers: authHeaders() });
      loadFiles();
    } catch (e) {}
  };

  return (
    <div style={{ marginTop: 6, marginBottom: 6 }}>
      {items.length > 0 && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 6 }}>
          {items.map((a) => (
            <div key={a.id} style={{ position: 'relative' }}>
              <img
                  src={a.url}
                  alt={a.filename}
                  title={`${a.filename} - @${a.uploaded_by}`}
                  style={{ width: 92, height: 92, objectFit: 'cover', borderRadius: 6, border: `0.5px solid ${colors.border}`, display: 'block' }}
                />
              <p style={{ fontSize: 9, color: colors.textMuted, margin: '2px 0 0', textAlign: 'center', maxWidth: 92 }}>
                {a.kind === 'brief' ? 'sample' : a.kind === 'revision' ? 'revision' : 'delivery'}
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, marginTop: 2 }}>
                <a
                  href={a.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={a.filename}
                  style={{ fontSize: 9, color: colors.primary, textDecoration: 'none',
                           border: `0.5px solid ${colors.border}`, borderRadius: 4,
                           padding: '1px 7px', background: '#fff' }}
                >
                  Open
                </a>
                <span className="longPressHint" style={{ fontSize: 8, color: colors.textMuted, textAlign: 'center' }}>
                  hold to save
                </span>
              </div>
              {canUpload && (
                <button
                  onClick={() => removeFile(a.id)}
                  title="Remove"
                  style={{ position: 'absolute', top: -6, right: -6, width: 18, height: 18, lineHeight: '16px', padding: 0, fontSize: 11, border: 'none', borderRadius: 9, background: '#fff', boxShadow: '0 0 3px rgba(0,0,0,0.3)', cursor: 'pointer' }}
                >
                  ×
                </button>
              )}
            </div>
          ))}
        </div>
      )}
      {items.some((a) => a.note) && (
        <div style={{ marginBottom: 6 }}>
          {items.filter((a) => a.note).map((a) => (
            <p key={a.id} style={{ fontSize: 11, color: colors.text, margin: '2px 0', background: '#F1EFE8', padding: '4px 6px', borderRadius: 4 }}>
              📝 {a.note} <span style={{ color: colors.textMuted }}>— @{a.uploaded_by}</span>
            </p>
          ))}
        </div>
      )}
      {canUpload && (
        <>
          <input
            value={attachNote}
            onChange={(e) => setAttachNote(e.target.value)}
            placeholder={kind === 'delivery' ? 'Note with this file (optional)' : 'Instructions for this image (optional)'}
            style={{ width: '100%', padding: '6px 8px', fontSize: 12, border: `0.5px solid ${colors.border}`, borderRadius: 6, marginBottom: 6, boxSizing: 'border-box' }}
          />
          <label style={{ fontSize: 12, color: colors.primary, cursor: 'pointer', display: 'inline-block' }}>
            {busy ? 'Uploading...' : '📎 Attach file'}
            <input type="file" accept="image/*" onChange={pickFile} disabled={busy} style={{ display: 'none' }} />
          </label>
        </>
      )}
      {chatWith && (
        <a
          href={`/chat/${chatWith}`}
          style={{ fontSize: 12, color: colors.primary, marginLeft: canUpload ? 12 : 0, display: 'inline-block' }}
        >
          💬 Discuss on chat
        </a>
      )}
      {err && <p style={{ fontSize: 11, color: colors.danger, margin: '4px 0 0' }}>{err}</p>}
    </div>
  );
}
